import React, { useState } from "react";
import styled from "@emotion/styled";
import BasicButton from "@components/button/BasicButton";
import LabelWithInput from "@components/input/LabelWithInput";
import CustomSelectBox from "@components/input/CustomSelectBox";
import { useFormContext } from "react-hook-form";

type TypeItem = {
  tag_id: number;
  label: string;
  rank?: number;
};

type TagItem = {
  id: number;
  name: string;
};

type Props = {
  typeOptions: TypeItem[];
  allTypeTags: TagItem[];
};

const TypeCategorySelector = ({ typeOptions, allTypeTags }: Props) => {
  const {
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();
  const [isCustomMode, setIsCustomMode] = useState(false);

  const selectedTypeTags: number[] = watch("tag_type") || [];
  const selectedTagId = selectedTypeTags[0] ?? null;
  const typeError = (errors as any)?.tag_type?.message as string | undefined;

  const handleTypeSelect = (tagId: number) => {
    if (selectedTagId === tagId) {
      setValue("tag_type", [], { shouldValidate: true });
      return;
    }

    setIsCustomMode(false);
    setValue("tag_type", [tagId], { shouldValidate: true });
  };

  const handleCustomMode = () => {
    setIsCustomMode(true);
    setValue("tag_type", [], { shouldValidate: true });
  };

  const handleAiMode = () => {
    setIsCustomMode(false);
    setValue("tag_type", [], { shouldValidate: true });
  };

  const handleDirectTypeSelect = (value: string | number) => {
    const numericValue = Number(value);
    if (!numericValue) {
      setValue("tag_type", [], { shouldValidate: true });
      return;
    }

    setValue("tag_type", [numericValue], { shouldValidate: true });
  };

  return (
    <div>
      <LabelWithInput label="속성 - 타입" required />
      <div style={{ paddingTop: "0.8rem" }} />

      {!isCustomMode ? (
        <ButtonContainer>
          {typeOptions.map((item) => (
            <BasicButton
              key={`${item.rank ?? "type"}-${item.tag_id}`}
              title={item.label}
              fit
              gray
              active={selectedTagId === item.tag_id}
              width="100%"
              fontSize="1.2rem"
              transparent
              padding="1rem"
              borderColor="#ccc"
              onClick={() => handleTypeSelect(item.tag_id)}
            />
          ))}
          <BasicButton
            title="직접 선택"
            fit
            gray
            active={isCustomMode}
            width="100%"
            fontSize="1.2rem"
            transparent
            padding="1rem"
            borderColor="#ccc"
            onClick={handleCustomMode}
          />
          {typeOptions.length === 0 && <HelperText>AI 추천 결과가 없습니다.</HelperText>}
        </ButtonContainer>
      ) : (
        <CustomSelectPanel>
          <BasicButton
            title="AI 추천으로 돌아가기"
            fit
            gray
            width="100%"
            fontSize="1.2rem"
            transparent
            padding="1rem"
            borderColor="#ccc"
            onClick={handleAiMode}
          />
          <CustomLabel>직접 타입 선택</CustomLabel>
          <CustomSelectBox
            value={selectedTagId ?? undefined}
            options={allTypeTags.map((tag) => ({
              label: tag.name,
              value: tag.id,
            }))}
            placeholder="타입을 선택해주세요."
            onChange={handleDirectTypeSelect}
          />
        </CustomSelectPanel>
      )}

      {typeError && <ErrorMessage>{typeError}</ErrorMessage>}
    </div>
  );
};

export default TypeCategorySelector;

const ButtonContainer = styled.div({
  display: "flex",
  flexWrap: "wrap",
  gap: "1rem",
  justifyContent: "flex-start",
  alignItems: "center",
});

const CustomSelectPanel = styled.div({
  display: "flex",
  flexDirection: "column",
  border: "1px solid #ccc",
  width: "100%",
  padding: "1rem",
  gap: "1rem",
  marginTop: "1rem",
});

const CustomLabel = styled.div({
  fontSize: "1.2rem",
  fontWeight: 600,
  color: "#666",
});

const ErrorMessage = styled.div(({ theme }) => ({
  color: theme.color.orange,
  fontSize: "1.2rem",
  marginTop: "0.8rem",
}));

const HelperText = styled.div(({ theme }) => ({
  color: theme.color.gray,
  fontSize: "1.2rem",
}));
